import { useCallback } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import orderBy from 'lodash.orderby';
import { useForm } from 'react-hook-form';

import {
  dummySKUsQuerySchema,
  type SKUFormSchema,
  type SKUQuerySchema,
} from '@/_pages/settings/inventory-kits/shared/schemas/skus';
import { dateTime, snackbar, useBoolean } from '@/_shared';

import type { SKUsProps } from './shared';
import { useCreateSKU } from './use-create-sku';

export type EditProps = SKUsProps & { open: boolean; onClose: () => void };

export function useEdit({ open, value, onClose, onChange: parentOnChange }: EditProps) {
  const [isLoading, loading] = useBoolean();

  const form = useForm({
    resolver: zodResolver(dummySKUsQuerySchema),
    defaultValues: { skus: value },
  });

  // keep dirty state on form update
  // and update form with sorted skus
  const updateFormWithKeepDirtyOn = useCallback(
    (skus: SKUFormSchema[]) => {
      const sortedSKUs = orderBy(skus, (item) => item?.pivot?.primary ?? 0, 'desc');
      form.setValue('skus', sortedSKUs, { shouldDirty: true });
      return sortedSKUs;
    },
    [form]
  );

  // handles defaults
  const handleDelete = useCallback(
    (sku: SKUQuerySchema) => {
      const formSKUs = form.getValues().skus;
      const newSKUs = formSKUs.filter((item) => item.id !== sku.id);

      return updateFormWithKeepDirtyOn(newSKUs);
    },
    [form, updateFormWithKeepDirtyOn]
  );

  const handleDefault = useCallback(
    (sku: SKUQuerySchema) => {
      const formSKUs = form.getValues().skus;
      const newSKUs = formSKUs.map((item) => {
        if (item.id === sku.id || item.id === sku.localMatchId) {
          return { ...item, ...sku };
        } else {
          return { ...item, pivot: { ...item.pivot, primary: 0 } };
        }
      });

      return updateFormWithKeepDirtyOn(newSKUs);
    },
    [form, updateFormWithKeepDirtyOn]
  );

  const alreadyExistCheck = useCallback(
    (sku: SKUQuerySchema) => {
      return [...new Set(form.getValues().skus.map((item) => item.sku))].includes(sku.sku);
    },
    [form]
  );

  // will be triggered from create sku form
  // after successful API response
  const handleCreateFromNewForm = useCallback(
    (sku: SKUQuerySchema) => {
      if (alreadyExistCheck(sku)) {
        console.log('handleCreateFromNewForm: SKU already exists');
        return -2;
      }

      if (sku.sku !== '') {
        const formSKUs = form.getValues().skus;
        const newSKUs = [...formSKUs, sku];
        updateFormWithKeepDirtyOn(newSKUs);
      }
      return 1;
    },
    [alreadyExistCheck, form, updateFormWithKeepDirtyOn]
  );

  // will be triggered from update sku form
  // after successful API response
  const handleUpdateSKUItem = useCallback(
    (nSKU: SKUFormSchema) => {
      if (nSKU.intent === 'delete') {
        return handleDelete(nSKU);
      }
      if (nSKU.pivot.primary === 1) {
        return handleDefault(nSKU);
      }

      // !important, to update latest values
      const fValues = form.getValues().skus;
      const nValue = fValues.map((item) => {
        // checking localMatchId
        // it is for create sku case
        if (item.id === nSKU.id || item.id === nSKU.localMatchId) {
          return nSKU;
        }
        return item;
      });
      return updateFormWithKeepDirtyOn([...nValue]);
    },
    [form, handleDefault, handleDelete, updateFormWithKeepDirtyOn]
  );

  const createProps = useCreateSKU({ onChange: handleCreateFromNewForm });

  const checkNewFormValidity = useCallback(
    async (nonEmptyCheck = true) => {
      const newSKU = await createProps.validateForm();

      let issue = '';
      if (!newSKU) {
        issue = 'Invalid SKU';
      } else if (alreadyExistCheck(newSKU)) {
        issue = 'SKU already exists.';
      } else if (nonEmptyCheck && newSKU.sku === '') {
        issue = 'Invalid SKU';
      }

      if (issue) {
        createProps.form.setError('sku', {
          type: 'manual',
          message: issue,
        });

        return { newSKU: undefined };
      }
      return { newSKU };
    },
    [alreadyExistCheck, createProps]
  );

  // does not submit just add into the update list
  // intent is to create
  // alternativeId should replaced by id
  const onAdd = useCallback(async () => {
    const { newSKU } = await checkNewFormValidity();

    if (newSKU) {
      updateFormWithKeepDirtyOn([...form.getValues().skus, { ...newSKU, localMatchId: newSKU.id }]);
      createProps.form.reset();
      createProps.form.setValue('id', dateTime.now().valueOf());
    }
  }, [checkNewFormValidity, createProps.form, form, updateFormWithKeepDirtyOn]);

  const onSubmit = useCallback(
    async (data: { skus: SKUFormSchema[] }) => {
      const { newSKU } = await checkNewFormValidity(false);
      if (!newSKU) {
        console.log('Invalid sku or sku already exists');
        return;
      }

      if (form.formState.isDirty) {
        loading.setTrue();
        parentOnChange(data.skus);
        loading.setFalse();
        onClose();
      } else {
        snackbar.error('No changes detected');
      }
    },
    [checkNewFormValidity, form.formState.isDirty, loading, parentOnChange, onClose]
  );

  // console.log(form.getValues().skus);

  return {
    open,
    form,
    isLoading,
    createProps,
    onClose,
    onSubmit,
    onAdd,
    handleUpdateSKUItem,
  };
}
